
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';

export default function Careers() {
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation First',
      description: 'We push boundaries and embrace cutting-edge intelligent technology to solve real problems.',
    },
    {
      icon: 'ri-team-line',
      title: 'Collaboration',
      description: 'Great ideas come from diverse perspectives. We work together to build something amazing.',
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Customer Obsessed',
      description: 'Our customers are at the heart of everything we do. Their success is our success.',
    },
    {
      icon: 'ri-rocket-line',
      title: 'Move Fast',
      description: 'We iterate quickly, learn from failures, and adapt. Speed is our competitive advantage.',
    },
  ];

  const benefits = [
    {
      icon: 'ri-health-book-line',
      title: 'Health & Wellness',
      description: 'Comprehensive health, dental, and vision insurance',
    },
    {
      icon: 'ri-time-line',
      title: 'Flexible Schedule',
      description: 'Work when you\'re most productive with flexible hours',
    },
    {
      icon: 'ri-home-office-line',
      title: 'Remote First',
      description: 'Work from anywhere in the world',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Unlimited PTO',
      description: 'Take the time you need to recharge',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Competitive Salary',
      description: 'Top-of-market compensation and equity',
    },
    {
      icon: 'ri-book-open-line',
      title: 'Learning Budget',
      description: '$2,000/year for courses, books, and conferences',
    },
    {
      icon: 'ri-macbook-line',
      title: 'Equipment',
      description: 'Latest MacBook Pro or PC and accessories',
    },
    {
      icon: 'ri-shield-check-line',
      title: '401(k) Match',
      description: '4% company match on retirement contributions',
    },
  ];

  const openPositions = [
    {
      id: 1,
      title: 'Senior AI Engineer',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      description: 'Build and scale our intelligent content generation engine using state-of-the-art LLMs.',
      tags: ['Python', 'Machine Learning', 'NLP'],
    },
    {
      id: 2,
      title: 'Full Stack Engineer',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      description: 'Develop features across our React/TypeScript frontend and Node.js backend.',
      tags: ['React', 'TypeScript', 'Node.js'],
    },
    {
      id: 3,
      title: 'Product Designer',
      department: 'Design',
      location: 'Remote',
      type: 'Full-time',
      description: 'Create beautiful, intuitive experiences that help users manage their social media effortlessly.',
      tags: ['Figma', 'UI/UX', 'Design Systems'],
    },
    {
      id: 4,
      title: 'Product Marketing Manager',
      department: 'Marketing',
      location: 'Remote',
      type: 'Full-time',
      description: 'Drive go-to-market strategy, positioning, and messaging for MandaPost.',
      tags: ['SaaS', 'Content', 'Growth'],
    },
    {
      id: 5,
      title: 'Customer Success Manager',
      department: 'Customer Success',
      location: 'Remote',
      type: 'Full-time',
      description: 'Help our customers achieve their social media goals and drive product adoption.',
      tags: ['Customer Success', 'SaaS', 'Communication'],
    },
    {
      id: 6,
      title: 'DevOps Engineer',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      description: 'Build and maintain our cloud infrastructure, CI/CD pipelines, and monitoring systems.',
      tags: ['AWS', 'Docker', 'Kubernetes'],
    },
    {
      id: 7,
      title: 'Content Marketing Manager',
      department: 'Marketing',
      location: 'Remote',
      type: 'Full-time',
      description: 'Create engaging content that educates, inspires, and converts our target audience.',
      tags: ['Content Strategy', 'SEO', 'Copywriting'],
    },
    {
      id: 8,
      title: 'Sales Development Representative',
      department: 'Sales',
      location: 'Remote',
      type: 'Full-time',
      description: 'Generate and qualify leads for our enterprise sales team.',
      tags: ['Outbound', 'SaaS Sales', 'Prospecting'],
    },
  ];

  const departments = ['all', 'Engineering', 'Design', 'Marketing', 'Customer Success', 'Sales'];

  const filteredPositions = selectedDepartment === 'all'
    ? openPositions
    : openPositions.filter(pos => pos.department === selectedDepartment);

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-white font-medium mb-6">
              <i className="ri-briefcase-line mr-2"></i>
              Join Our Team
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Build the Future of
              <span className="block">Social Media with AI</span>
            </h1>

            <p className="text-xl lg:text-2xl text-white/90 mb-10 leading-relaxed">
              Join a fast-growing startup revolutionizing how businesses manage their social media. We're building the world's first AI-powered social media planning platform.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#open-positions"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
              >
                <i className="ri-search-line"></i>
                View Open Positions
              </a>
              <a
                href="#culture"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-all cursor-pointer"
              >
                <i className="ri-team-line"></i>
                Our Culture
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { number: '25+', label: 'Team Members' },
              { number: '10+', label: 'Countries' },
              { number: '$15M', label: 'Series A Funding' },
              { number: '100%', label: 'Remote Team' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-[#22577A] mb-2">{stat.number}</div>
                <div className="text-gray-600 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10" id="culture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide everything we do at MandaPost
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-xl transition-all text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className={`${value.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#22577A] mb-3">{value.title}</h3>
                <p className="text-gray-700 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Benefits & Perks
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We take care of our team so they can do their best work
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-xl p-6 border-2 border-[#66b3ff]/30 hover:border-[#66b3ff] hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center mb-4">
                  <i className={`${benefit.icon} text-white text-2xl`}></i>
                </div>
                <h4 className="text-lg font-bold text-[#22577A] mb-2">{benefit.title}</h4>
                <p className="text-gray-700 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10" id="open-positions">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Open Positions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Join our team and help shape the future of social media management
            </p>

            {/* Department Filter */}
            <div className="flex flex-wrap gap-3 justify-center">
              {departments.map((dept) => (
                <button
                  key={dept}
                  onClick={() => setSelectedDepartment(dept)}
                  className={`px-6 py-2 rounded-full font-semibold transition-all ${
                    selectedDepartment === dept
                      ? 'bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white shadow-lg'
                      : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-[#66b3ff]'
                  }`}
                >
                  {dept === 'all' ? 'All Departments' : dept}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {filteredPositions.map((position) => (
              <div
                key={position.id}
                className="bg-white rounded-2xl p-6 md:p-8 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold text-[#22577A] group-hover:text-[#38a3a5] transition-colors">
                        {position.title}
                      </h3>
                      <span className="px-3 py-1 bg-[#e4f4ff]/30 text-[#22577A] rounded-full text-sm font-semibold">
                        {position.type}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mb-3 text-gray-600">
                      <span className="flex items-center gap-1">
                        <i className="ri-briefcase-line"></i>
                        {position.department}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line"></i>
                        {position.location}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {position.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {position.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <a
                      href="/contact"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                    >
                      Apply Now
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredPositions.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-600">No positions found in this department.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-mail-line text-4xl text-white"></i>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Don't See the Right Role?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            We're always looking for talented people to join our team. Send us your resume and let us know what you're passionate about.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
          >
            <i className="ri-send-plane-line"></i>
            Send Us Your Resume
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
